/**
 * Bay equipment configuration (1.4 P17): controllers, simple-mode relay
 * mapping (tiers / upsells / coin + timer), input assignments and behavior,
 * all through GET/PUT /api/bays/:id/config. Advanced mode (multi-step
 * programs) is shown read-only here; its editor lands in P18.
 *
 * Works for owners and admins acting for an owner through useOwnerScope.
 */
import React, { forwardRef, useImperativeHandle, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Switch, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useOwnerScope } from '../../context/OwnerScopeContext';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../Toast';
import Input from '../Input';
import InlineConfirm from '../InlineConfirm';
import { useRouter } from 'expo-router';
import ControllerPicker from '../ControllerPicker';
import CollapsibleSection from '../CollapsibleSection';
import Picker from '../Picker';
import Button from '../Button';
import AdvancedActionsEditor from './AdvancedActionsEditor';
import type { ColorTheme } from '../../utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../utils/theme';

import type { Step, Action as EditorAction } from './AdvancedActionsEditor';
type Action = EditorAction;
interface CtrlRow { controllerId: string; slot: number; label: string | null; name: string | null; model: string; modelLabel: string; relayCount: number; inputCount: number; online: boolean; firmware: string | null; capabilities: string[]; inputsState: number[] }
interface InputRow { controllerId: string; inputIndex: number; function: string; activeWhen: string; label: string | null; debounceMs?: number; config?: Record<string, unknown> }
interface Config {
  bay: { id: string; type: string; configMode: 'simple' | 'advanced'; allowPurchaseWhileInUse: boolean; inUseMaxWaitMs: number; inUseStartDelayMs?: number; oosMessage: string | null; oosMessageEffective: string; oosAlert: { alertOnOut?: boolean; alertOnBack?: boolean; flapWindowSec?: number }; liveState: Record<string, unknown> };
  machine?: { id: string; name: string; configMode: string; creditStepCents: number; maxCreditCents: number; presets: number[] | null };
  controllers: CtrlRow[]; actions: Action[]; inputs: InputRow[]; relayMap: Record<string, Record<string, string[]>>; warnings: { code: string; controllerId: string; message: string }[];
}
interface Available { id: string; name: string | null; model: string; modelLabel: string; relayCount: number; inputCount: number; online: boolean; freeRelays: number[]; assignedTo: { kind: string; id: string; slot: number }[] }
interface WashTier { id: string; name: string; priceInCents: number; isActive: boolean }
interface Upsell { id: string; name: string; priceCents: number; defaultDelayMs: number; isActive: boolean }

interface SimpleRow { enabled: boolean; controllerId: string; relay: string; pulseMs: string; priceOverride: string; delaySec: string }
/** R03 simple automatic: what each relay of each board drives. value = 'tier:<id>' | 'upsell:<id>' | '' */
interface RelayAssign { value: string; delaySec: string }
const INPUT_FUNCTIONS = [
  { value: 'none', label: 'Not used' },
  { value: 'in_operation', label: 'Wash in progress' },
  { value: 'out_of_service', label: 'In service' },
  { value: 'coin_pulse', label: 'Coin / cash pulse counter' },
];

interface Props { bayId: string; bayType: string; onSaved?: () => void; /** Round 2 (S05): vending machines reuse this screen with their own endpoints. */ apiBase?: string; /** Round 2: the parent screen owns the single Save button and calls save() through the ref. */ hideSave?: boolean }
export interface BayEquipmentConfigHandle { save: () => Promise<boolean> }

const BayEquipmentConfig = forwardRef<BayEquipmentConfigHandle, Props>(function BayEquipmentConfig({ bayId, bayType, onSaved, apiBase, hideSave }: Props, ref) {
  const base = apiBase ?? `/api/bays/${bayId}`;
  const isVending = bayType === 'vending';
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const toast = useToast();
  const scope = useOwnerScope();
  const router = useRouter();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState<Config | null>(null);
  const [available, setAvailable] = useState<Available[]>([]);
  const [tiers, setTiers] = useState<WashTier[]>([]);
  const [upsells, setUpsells] = useState<Upsell[]>([]);
  const [issues, setIssues] = useState<string[]>([]);
  // editable state
  const [ctrls, setCtrls] = useState<CtrlRow[]>([]);
  const [mode, setMode] = useState<'simple' | 'advanced'>('simple');
  const [tierRows, setTierRows] = useState<Record<string, SimpleRow>>({});
  const [upsellRows, setUpsellRows] = useState<Record<string, SimpleRow>>({});
  const [coin, setCoin] = useState({ controllerId: '', relay: '1', pulseMs: '70', gapMs: '70', coinValueCents: '25' });
  const [timer, setTimer] = useState({ controllerId: '', relay: '2' });
  // R03: self-serve / vacuum simple mode = one of coin drop | constant (timer) | none; automatic = per-relay assignments.
  const [ssMode, setSsMode] = useState<'none' | 'coin' | 'timer'>('none');
  const [relayAssign, setRelayAssign] = useState<Record<string, RelayAssign>>({}); // key `${controllerId}:${relay}`
  const [inputs, setInputs] = useState<InputRow[]>([]);
  const [allowQueue, setAllowQueue] = useState(true);
  const [startDelayMs, setStartDelayMs] = useState('0'); // round 2: 0 = purchases blocked while in use
  const [maxWaitMin, setMaxWaitMin] = useState('15');
  const [modeConfirm, setModeConfirm] = useState<'simple' | 'advanced' | null>(null);
  const [oosMessage, setOosMessage] = useState('');
  const [alertOnOut, setAlertOnOut] = useState(true);
  const [alertOnBack, setAlertOnBack] = useState(true);
  const [flapSec, setFlapSec] = useState('300');
  const [addPick, setAddPick] = useState('');
  const [advancedActions, setAdvancedActions] = useState<Action[]>([]);

  const isAutomatic = bayType === 'automatic';
  const blank = (): SimpleRow => ({ enabled: false, controllerId: '', relay: '', pulseMs: '300', priceOverride: '', delaySec: '' });

  const hydrate = useCallback((c: Config, tierCat: WashTier[], upCat: Upsell[]) => {
    setCfg(c);
    setCtrls(c.controllers);
    setMode(c.bay.configMode);
    setAllowQueue(c.bay.allowPurchaseWhileInUse);
    setMaxWaitMin(String(Math.round(c.bay.inUseMaxWaitMs / 60000)));
    setStartDelayMs(String(c.bay.inUseStartDelayMs ?? (c.bay.allowPurchaseWhileInUse ? 1000 : 0)));
    setOosMessage(c.bay.oosMessage ?? '');
    setAlertOnOut(c.bay.oosAlert?.alertOnOut !== false);
    setAlertOnBack(c.bay.oosAlert?.alertOnBack !== false);
    setFlapSec(String(c.bay.oosAlert?.flapWindowSec ?? 300));
    setInputs(c.inputs);
    setAdvancedActions(c.bay.configMode === 'advanced' ? c.actions : []); // Advanced never shows Simple rows
    const firstRelay = (a: Action) => a.steps.find(s => s.type === 'relays') as Extract<Step, { type: 'relays' }> | undefined;
    const tr: Record<string, SimpleRow> = {};
    for (const t of tierCat) {
      const a = c.actions.find(x => x.kind === 'tier' && x.tierId === t.id);
      const st = a ? firstRelay(a) : undefined;
      tr[t.id] = a && st ? { enabled: a.enabled !== false, controllerId: st.controllerId, relay: String(st.relays[0] ?? ''), pulseMs: String(st.onMs || 300), priceOverride: '', delaySec: '' } : blank();
    }
    setTierRows(tr);
    const ur: Record<string, SimpleRow> = {};
    for (const u of upCat) {
      const a = c.actions.find(x => x.kind === 'upsell' && x.upsellId === u.id);
      const st = a ? firstRelay(a) : undefined;
      ur[u.id] = a && st ? { enabled: a.enabled !== false, controllerId: st.controllerId, relay: String(st.relays[0] ?? ''), pulseMs: String(st.onMs || 300), priceOverride: '', delaySec: a.delayAfterTierMs ? String(a.delayAfterTierMs / 1000) : '' } : { ...blank(), delaySec: u.defaultDelayMs ? String(u.defaultDelayMs / 1000) : '' };
    }
    setUpsellRows(ur);
    const coinA = c.actions.find(x => x.kind === 'coin_drop'); const coinS = coinA ? firstRelay(coinA) : undefined;
    const timA = c.actions.find(x => x.kind === 'timer'); const timS = timA ? firstRelay(timA) : undefined;
    const cc = (coinA?.config ?? {}) as any;
    setCoin({ controllerId: coinS?.controllerId ?? c.controllers[0]?.controllerId ?? '', relay: String(coinS?.relays[0] ?? 1), pulseMs: String(cc.pulseMs ?? 70), gapMs: String(cc.gapMs ?? 70), coinValueCents: String(cc.coinValueCents ?? 25) });
    setTimer({ controllerId: timS?.controllerId ?? c.controllers[0]?.controllerId ?? '', relay: String(timS?.relays[0] ?? 2) });
    setSsMode(coinA && coinA.enabled !== false ? 'coin' : timA && timA.enabled !== false ? 'timer' : 'none');
    const ra: Record<string, RelayAssign> = {};
    for (const a of c.actions) {
      if (a.kind !== 'tier' && a.kind !== 'upsell') continue;
      const st = firstRelay(a); if (!st) continue;
      ra[`${st.controllerId}:${st.relays[0]}`] = { value: a.kind === 'tier' ? `tier:${a.tierId}` : `upsell:${a.upsellId}`, delaySec: a.delayAfterTierMs ? String(a.delayAfterTierMs / 1000) : '' };
    }
    setRelayAssign(ra);
  }, []);

  const toastRef = useRef(toast); toastRef.current = toast;
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [c, av, tc, uc] = await Promise.all([
        scope.api.get<Config>(`${base}/config`),
        scope.api.get<{ controllers: Available[] }>('/api/controllers/available').then(r => r.controllers).catch(() => [] as Available[]),
        scope.api.get<WashTier[]>('/api/wash-tiers').then(l => l.filter(t => t.isActive)).catch(() => [] as WashTier[]),
        scope.api.get<Upsell[]>('/api/upsells').then(l => l.filter(u => u.isActive)).catch(() => [] as Upsell[]),
      ]);
      setAvailable(av); setTiers(tc); setUpsells(uc);
      hydrate(c, tc, uc);
    } catch (err: any) {
      toastRef.current.show({ type: 'error', title: 'Could not load bay configuration', message: err.message });
    } finally { setLoading(false); }
  // toast is read through a ref on purpose: its identity changes on every toast, and having it here re-ran load()
  // after e.g. "Board removed", re-hydrating from the server and silently wiping unsaved edits (found 10-20).
  }, [bayId, scope.api, hydrate]);
  useEffect(() => { load(); }, [load]);

  // ---- controllers ----
  const addController = (id: string) => {
    if (!id || ctrls.some(c => c.controllerId === id)) return;
    const a = available.find(x => x.id === id);
    if (!a) return;
    // Round 2: boards with inputs come pre-filled (editable): Input 1 = In service (signal = in service), Input 2 = Wash in progress (signal = washing).
    if ((a.inputCount ?? 0) >= 2 && !inputs.some(i => i.controllerId === a.id)) {
      setInputs([...inputs,
        { controllerId: a.id, inputIndex: 1, function: 'out_of_service', activeWhen: 'no_signal', label: 'In service' },
        { controllerId: a.id, inputIndex: 2, function: 'in_operation', activeWhen: 'signal', label: 'Wash in progress' },
      ]);
    }
    setCtrls([...ctrls, { controllerId: a.id, slot: ctrls.length, label: null, name: a.name, model: a.model, modelLabel: a.modelLabel, relayCount: a.relayCount, inputCount: a.inputCount, online: a.online, firmware: null, capabilities: [], inputsState: [] }]);
    setAddPick('');
  };
  /** Round 2: removing a board drops whatever referenced it (relay rows, coin / constant relay, Advanced steps) instead of blocking. */
  const removeController = (id: string) => {
    let dropped = 0;
    if (mode === 'simple') {
      const next: Record<string, RelayAssign> = {};
      for (const [k, v] of Object.entries(relayAssign)) { if (k.startsWith(`${id}:`)) { if (v.value) dropped++; } else next[k] = v; }
      setRelayAssign(next);
      if ((ssMode === 'coin' && coin.controllerId === id) || (ssMode === 'timer' && timer.controllerId === id)) { setSsMode('none'); dropped++; }
    } else {
      const next = advancedActions.map(a => {
        const steps = a.steps
          .filter(s => !(s.type !== 'wait' && s.controllerId === id && !((s.type === 'relays' || s.type === 'hold' || s.type === 'release') && (s.also ?? []).some(b => b.controllerId !== id))))
          .map(s => {
            if ((s.type === 'relays' || s.type === 'hold' || s.type === 'release') && (s.controllerId === id || (s.also ?? []).some(b => b.controllerId === id))) {
              const boards = [{ controllerId: s.controllerId, relays: s.relays }, ...(s.also ?? [])].filter(b => b.controllerId !== id);
              const [primary, ...rest] = boards;
              const out = { ...s, controllerId: primary.controllerId, relays: primary.relays } as typeof s;
              if (rest.length) (out as any).also = rest; else delete (out as any).also;
              return out;
            }
            return s;
          });
        if (steps.length !== a.steps.length) dropped++;
        return { ...a, steps };
      }).filter(a => a.steps.some(s => s.type !== 'wait' && s.type !== 'wait_input'));
      if (next.length !== advancedActions.length) dropped += advancedActions.length - next.length;
      setAdvancedActions(next);
    }
    setCtrls(ctrls.filter(c => c.controllerId !== id).map((c, i) => ({ ...c, slot: i })));
    setInputs(inputs.filter(i => i.controllerId !== id));
    if (dropped) toast.show({ type: 'info', title: 'Board removed', message: `${dropped} relay assignment${dropped === 1 ? '' : 's'} that used it ${dropped === 1 ? 'was' : 'were'} cleared. Save to apply.` });
  };
  const relayOptions = (controllerId: string, selfKey: string) => {
    const c = ctrls.find(x => x.controllerId === controllerId);
    const n = c?.relayCount ?? 4;
    const usage: Record<string, string> = {};
    if (!isAutomatic) { if (ssMode === 'coin' && coin.controllerId === controllerId && selfKey !== 'coin') usage[coin.relay] = 'Coin drop'; if (ssMode === 'timer' && timer.controllerId === controllerId && selfKey !== 'timer') usage[timer.relay] = 'Constant'; }
    const foreign = available.find(a => a.id === controllerId);
    return [{ value: '', label: 'Relay…' }, ...Array.from({ length: n }, (_, i) => String(i + 1)).map(r => {
      const taken = usage[r] ? ` — ${usage[r]}` : (foreign && !foreign.freeRelays.includes(Number(r)) && !(cfg?.relayMap?.[controllerId]?.[r]) ? ' — used by another bay' : '');
      return { value: r, label: `Relay ${r}${taken}` };
    })];
  };
  const ctrlOptions = [{ value: '', label: 'Controller…' }, ...ctrls.map(c => ({ value: c.controllerId, label: `${c.name || c.controllerId} (${c.modelLabel})` }))];

  // ---- build + save ----
  const buildActions = (): Action[] | null => {
    if (mode === 'advanced') return advancedActions;
    const out: Action[] = [];
    if (isAutomatic) {
      let order = 0;
      for (const [key, ra] of Object.entries(relayAssign)) {
        if (!ra.value) continue;
        const [controllerId, relayStr] = key.split(':'); const relay = Number(relayStr);
        const [kind, id] = ra.value.split(':');
        if (kind === 'tier') {
          const t = tiers.find(x => x.id === id); if (!t) continue;
          out.push({ kind: 'tier', name: t.name, tierId: t.id, displayOrder: order++, enabled: true, steps: [{ type: 'relays', controllerId, relays: [relay], onMs: 300 }] });
        } else {
          const u = upsells.find(x => x.id === id); if (!u) continue;
          const delay = ra.delaySec.trim() ? Math.round(parseFloat(ra.delaySec) * 1000) : 0;
          out.push({ kind: 'upsell', name: u.name, upsellId: u.id, priceCents: null, displayOrder: order++, enabled: true, delayAfterTierMs: Number.isFinite(delay) ? delay : 0, steps: [{ type: 'relays', controllerId, relays: [relay], onMs: 300 }] });
        }
      }
    } else if (ssMode === 'coin') {
      if (!coin.controllerId || !coin.relay) { toast.show({ type: 'error', title: 'Pick the board and relay for the coin drop' }); return null; }
      out.push({ kind: 'coin_drop', name: isVending ? 'Credit pulses' : 'Coin drop', displayOrder: 0, enabled: true, steps: [{ type: 'relays', controllerId: coin.controllerId, relays: [Number(coin.relay)], onMs: 70 }], config: { pulseMs: 70, gapMs: 70, coinValueCents: isVending ? (cfg?.machine?.creditStepCents ?? 25) : (parseInt(coin.coinValueCents) || 25) } });
    } else if (ssMode === 'timer') {
      if (!timer.controllerId || !timer.relay) { toast.show({ type: 'error', title: 'Pick the board and relay for the constant output' }); return null; }
      out.push({ kind: 'timer', name: 'Constant (timer hold)', displayOrder: 0, enabled: true, steps: [{ type: 'relays', controllerId: timer.controllerId, relays: [Number(timer.relay)], onMs: 0 }] });
    }
    return out;
  };

  const save = async (): Promise<boolean> => {
    let saved = false;
    const actions = buildActions(); if (!actions) return false;
    const wait = parseInt(maxWaitMin);
    if (!isVending && (isNaN(wait) || wait < 1 || wait > 60)) { toast.show({ type: 'error', title: 'Max wait must be 1–60 minutes' }); return false; } // vending has no queue (field hidden), 10-16 review H8
    const delayMs = isAutomatic ? (parseInt(startDelayMs) || 0) : 0;
    if (delayMs < 0 || delayMs > 600000) { toast.show({ type: 'error', title: 'Start delay must be 0–600000 ms' }); return false; }
    if (isAutomatic && tiers.length === 0 && mode === 'simple') { toast.show({ type: 'error', title: 'Set up wash tiers first', message: 'Automatic equipment needs at least one wash tier on the Wash Tiers & Upsells page.' }); return false; }
    if (mode === 'simple' && ctrls.length > 1) { toast.show({ type: 'error', title: 'Simple mode uses one board', message: 'Remove the extra boards or switch to Advanced.' }); return false; }
    const flap = parseInt(flapSec);
    if (isNaN(flap) || flap < 0 || flap > 3600) { toast.show({ type: 'error', title: 'Ignore window must be 0–3600 seconds' }); return false; }
    setSaving(true); setIssues([]);
    try {
      const body = {
        bay: { configMode: mode, allowPurchaseWhileInUse: isAutomatic ? delayMs > 0 : false, inUseStartDelayMs: delayMs, inUseMaxWaitMs: wait * 60000, oosMessage: oosMessage.trim() || null, oosAlert: { alertOnOut, alertOnBack, flapWindowSec: flap } },
        controllers: ctrls.map(c => ({ controllerId: c.controllerId, slot: c.slot, label: c.label })),
        actions,
        inputs: inputs.filter(i => i.function !== 'none').map(i => ({ controllerId: i.controllerId, inputIndex: i.inputIndex, function: i.function, activeWhen: i.activeWhen, label: i.label, debounceMs: i.debounceMs ?? 30, config: i.config ?? {} })),
      };
      const c = await scope.api.put<Config>(`${base}/config`, body);
      hydrate(c, tiers, upsells);
      if (!hideSave) toast.show({ type: 'success', title: 'Equipment configuration saved' });
      saved = true;
      onSaved?.();
    } catch (err: any) {
      const list: string[] = Array.isArray(err?.details?.issues) ? err.details.issues.map((i: any) => i.message) : [];
      setIssues(list);
      toast.show({ type: 'error', title: 'Could not save', message: list[0] || err.message });
    } finally { setSaving(false); }
    return saved;
  };

  useImperativeHandle(ref, () => ({ save }), [save]);

  if (loading) return <View style={styles.center}><ActivityIndicator color={colors.primary} /></View>;
  if (!cfg) return null;
  // Queue section helpers (Nathan 10-18): which board/input carries the Wash in progress signal.
  const inputBoards = ctrls.filter(c => (c.inputCount ?? 0) > 0);
  const inOpRow = inputs.find(i => i.function === 'in_operation');
  const inOpValue = inOpRow ? `${inOpRow.controllerId}:${inOpRow.inputIndex}` : '';
  const inOpOptions = [{ value: '', label: 'Not set' }, ...inputBoards.flatMap(c => Array.from({ length: c.inputCount }, (_, k) => ({ value: `${c.controllerId}:${k + 1}`, label: `${c.name || c.controllerId} · Input ${k + 1}` })))];
  const setInOpInput = (v: string) => {
    const [cid, idxStr] = v.split(':'); const idx = parseInt(idxStr);
    const cleared = inputs.map(i => i.function === 'in_operation' ? { ...i, function: 'none', label: null } : i);
    if (!v) { setInputs(cleared); return; }
    const existing = cleared.find(i => i.controllerId === cid && i.inputIndex === idx);
    const row: InputRow = { ...(existing ?? { controllerId: cid, inputIndex: idx, activeWhen: 'signal', label: null }), function: 'in_operation', label: 'Wash in progress' };
    setInputs([...cleared.filter(i => !(i.controllerId === cid && i.inputIndex === idx)), row]);
  };
  const inputRow = (controllerId: string, index: number): InputRow => inputs.find(i => i.controllerId === controllerId && i.inputIndex === index) ?? { controllerId, inputIndex: index, function: 'none', activeWhen: 'signal', label: null };
  const setInput = (controllerId: string, index: number, patch: Partial<InputRow>) => {
    const cur = inputRow(controllerId, index);
    setInputs([...inputs.filter(i => !(i.controllerId === controllerId && i.inputIndex === index)), { ...cur, ...patch }]);
  };
  const renderBoardInputs = (c: CtrlRow) => (c.inputCount ?? 0) > 0 ? (
    <CollapsibleSection title={`${c.name || c.controllerId} inputs`} subtitle={`${inputs.filter(i => i.controllerId === c.controllerId && i.function !== 'none').length} of ${c.inputCount} in use`} icon="git-branch-outline" style={styles.boardInputs}>
      <Text style={styles.help}>Inputs 1 and 2 are pre-set to In service and Wash in progress; change any of them here. "Signal = active" means the terminal is energized when active.</Text>
      {Array.from({ length: c.inputCount }, (_, i) => i + 1).map(n => { const r = inputRow(c.controllerId, n); const lvl = c.inputsState?.[n - 1]; return (
        <View key={`${c.controllerId}:${n}`} style={styles.inputRow}>
          <View style={styles.inputHead}>
            <View style={[styles.dot, { backgroundColor: lvl === 1 ? colors.primary : colors.borderLight }]} />
            <Text style={styles.mapName}>Input {n}{r.label ? ` · ${r.label}` : ''}</Text>
          </View>
          <Picker options={INPUT_FUNCTIONS} value={r.function} onValueChange={v => setInput(c.controllerId, n, { function: v, label: v === 'out_of_service' ? 'In service' : v === 'in_operation' ? 'Wash in progress' : r.label })} />
          {r.function !== 'none' && (
            <View style={styles.mapFields}>
              <View style={styles.segment}>
                {(['signal', 'no_signal'] as const).map(p => (
                  <TouchableOpacity key={p} style={[styles.segBtn, r.activeWhen === p && styles.segBtnActive]} onPress={() => setInput(c.controllerId, n, { activeWhen: p })}>
                    <Text style={[styles.segText, r.activeWhen === p && styles.segTextActive]}>{p === 'signal' ? 'Signal = active' : 'No signal = active'}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              {r.function === 'coin_pulse' && (
                <View style={{ flex: 1 }}><Input label="¢ per pulse" value={String((r.config as any)?.valueCents ?? '')} onChangeText={v => setInput(c.controllerId, n, { config: { ...(r.config ?? {}), valueCents: parseInt(v.replace(/[^0-9]/g, '')) || 0 } })} keyboardType="number-pad" /></View>
              )}
            </View>
          )}
        </View>
      ); })}
    </CollapsibleSection>
  ) : null;

  const live = cfg.bay.liveState as { inUse?: boolean; outOfService?: boolean };

  return (
    <View>
      {/* ---- Mode (round 2: right under the Controller Setup header; switching clears the other mode after a confirm) ---- */}
      <View style={styles.segment}>
        {(['simple', 'advanced'] as const).map(m => (
          <TouchableOpacity key={m} style={[styles.segBtn, mode === m && styles.segBtnActive]}
            onPress={() => { if (m !== mode) setModeConfirm(m); }}>
            <Text style={[styles.segText, mode === m && styles.segTextActive]}>{m === 'simple' ? 'Simple' : 'Advanced'}</Text>
          </TouchableOpacity>
        ))}
      </View>
      {modeConfirm && (
        <InlineConfirm visible message={`Switching to ${modeConfirm === 'simple' ? 'Simple' : 'Advanced'} clears your ${mode === 'simple' ? 'Simple' : 'Advanced'} setup on this ${bayType === 'vacuum' ? 'vacuum' : 'bay'}. Continue?`} confirmLabel="Switch" onCancel={() => setModeConfirm(null)}
          onConfirm={() => {
            const m = modeConfirm; setModeConfirm(null);
            if (m === 'advanced') { setRelayAssign({}); setSsMode('none'); setAdvancedActions([]); }
            else { setAdvancedActions([]); if (ctrls.length > 1) { setCtrls(ctrls.slice(0, 1)); setInputs(inputs.filter(i => i.controllerId === ctrls[0].controllerId)); } }
            setMode(m);
          }} />
      )}
      {cfg.warnings.map(w => <Text key={w.controllerId + w.code} style={styles.warn}>{w.message}</Text>)}

      {/* ---- Boards: one in Simple, any number in Advanced ---- */}
      {mode === 'advanced' && <Text style={styles.help}>Controllers can be configured for use with 1 or more pieces equipment. Also multiple controllers can be configured for use with a single piece of equipment. Individual relays cannot be used with multiple pieces of equipment.</Text>}
      {ctrls.length === 0 && <Text style={styles.empty}>No controller assigned yet.</Text>}
      {ctrls.map(c => (
        <View key={c.controllerId} style={styles.ctrlRow}>
          <View style={[styles.dot, { backgroundColor: c.online ? colors.success : colors.danger }]} />
          <View style={{ flex: 1 }}>
            <Text style={styles.ctrlName}>{c.name || c.controllerId}</Text>
            <Text style={styles.ctrlMeta}>{c.modelLabel} · {c.relayCount} relays{c.inputCount ? ` · ${c.inputCount} inputs` : ''}{c.firmware ? ` · ${c.firmware}` : ''}</Text>
          </View>
          <TouchableOpacity onPress={() => removeController(c.controllerId)} hitSlop={8}><Ionicons name="close-circle-outline" size={20} color={colors.danger} /></TouchableOpacity>
        </View>
      ))}
      {ctrls.map(c => <React.Fragment key={`in-${c.controllerId}`}>{renderBoardInputs(c)}</React.Fragment>)}
      {(mode === 'advanced' || ctrls.length === 0) && (
        <ControllerPicker label={mode === 'simple' ? 'Controller' : 'Add controller'} placeholder="Add a claimed controller" value={addPick}
          targetLabel={isVending ? 'this machine' : 'this bay'} excludeBayId={isVending ? undefined : bayId} excludeMachineId={isVending ? bayId : undefined}
          allowShared hideNone excludeIds={ctrls.map(c => c.controllerId)} onChange={id => { if (id) addController(id); }} />
      )}

      {isAutomatic && tiers.length === 0 && (
        <View style={styles.tierPrompt}>
          <Ionicons name="information-circle" size={20} color={colors.primary} />
          <Text style={styles.tierPromptText}>Automatic equipment needs wash tiers before relays can be assigned. Set them up on Wash Tiers & Upsells, then come back here.</Text>
          <Button title="Wash Tiers & Upsells" variant="outline" size="sm" onPress={() => router.push(`${scope.basePath ?? '/owner'}/pricing/washes-upsells` as never)} />
        </View>
      )}
      <Text style={styles.sectionTitle}>Relay configuration</Text>

      {mode === 'advanced' ? (
        <AdvancedActionsEditor
          bayId={bayId}
          apiBase={base}
          bayType={bayType}
          ctrls={ctrls.map(c => ({ controllerId: c.controllerId, name: c.name, modelLabel: c.modelLabel, relayCount: c.relayCount, inputCount: c.inputCount, online: c.online }))}
          inputs={inputs.map(i => ({ controllerId: i.controllerId, inputIndex: i.inputIndex, function: i.function, label: i.label }))}
          actions={advancedActions as any}
          onChange={a => setAdvancedActions(a as Action[])}
          catalog={{ tiers, upsells }}
          foreignUse={Object.fromEntries(available.map(a => [a.id, Array.from({ length: a.relayCount }, (_, i) => i + 1).filter(r => !a.freeRelays.includes(r) && !cfg.relayMap?.[a.id]?.[String(r)])]))}
          dirty={JSON.stringify(advancedActions) !== JSON.stringify(cfg.actions) || JSON.stringify(ctrls.map(c => c.controllerId)) !== JSON.stringify(cfg.controllers.map(c => c.controllerId))}
        />
      ) : isAutomatic ? (
        <>
          <Text style={styles.help}>Each relay drives one wash tier or one upsell. Pulse timing uses the board defaults; use Advanced for custom timing or multi-step programs.</Text>
          {ctrls.length === 0 && <Text style={styles.empty}>Add a board above first.</Text>}
          {tiers.length > 0 && ctrls.map(c => {
            const foreign = available.find(a => a.id === c.controllerId);
            const assignOptions = [{ value: '', label: 'Unused' }, ...tiers.map(t => ({ value: `tier:${t.id}`, label: `Tier · ${t.name} ($${(t.priceInCents / 100).toFixed(2)})` })), ...upsells.map(u => ({ value: `upsell:${u.id}`, label: `Upsell · ${u.name} ($${(u.priceCents / 100).toFixed(2)})` }))];
            return (
              <View key={c.controllerId} style={styles.boardCard}>
                <View style={styles.boardHead}>
                  <View style={[styles.dot, { backgroundColor: c.online ? colors.success : colors.danger }]} />
                  <Text style={styles.boardTitle}>{c.name || c.controllerId}</Text>
                  <View style={styles.modelBadge}><Text style={styles.modelBadgeText}>{c.modelLabel} · {c.relayCount} relays</Text></View>
                </View>
                {Array.from({ length: c.relayCount }, (_, i) => i + 1).map(r => {
                  const key = `${c.controllerId}:${r}`; const ra = relayAssign[key] ?? { value: '', delaySec: '' };
                  const takenElsewhere = !!foreign && !foreign.freeRelays.includes(r) && !cfg?.relayMap?.[c.controllerId]?.[String(r)];
                  const usedTwice = ra.value && Object.entries(relayAssign).some(([k, v]) => k !== key && v.value === ra.value && v.value.startsWith('tier:'));
                  return (
                    <View key={key} style={styles.relayRow}>
                      <Text style={styles.relayLabel}>R{r}</Text>
                      <View style={{ flex: 1 }}>
                        {takenElsewhere ? <Text style={styles.empty}>Used by another bay</Text> : (
                          <Picker options={assignOptions} value={ra.value} onValueChange={v => setRelayAssign({ ...relayAssign, [key]: { ...ra, value: v } })} />
                        )}
                        {usedTwice ? <Text style={styles.warn}>This tier is already on another relay</Text> : null}
                      </View>
                      {ra.value.startsWith('upsell:') && (
                        <View style={{ width: 110 }}><Input label="Delay (s)" value={ra.delaySec} onChangeText={v => setRelayAssign({ ...relayAssign, [key]: { ...ra, delaySec: v.replace(/[^0-9.]/g, '') } })} keyboardType="decimal-pad" /></View>
                      )}
                    </View>
                  );
                })}
              </View>
            );
          })}
          {tiers.length === 0 && <Text style={styles.empty}>No wash tiers in your catalog yet (Wash Tiers & Upsells).</Text>}
        </>
      ) : (
        <>
          <Text style={styles.help}>{isVending ? 'Credit is delivered as coin pulses on one relay (credit per pulse is set on the machine above). Pick the board and relay.' : `${bayType === 'vacuum' ? 'How the vacuum is started: ' : 'How the bay is started: '}coin drop pulses the timer's coin input; constant holds a relay on for the whole session (count-up / credit-card input). Pick one, or none to free the relays for Advanced actions.`}</Text>
          <View style={styles.segment}>
            {([['none', 'None'], ['coin', 'Coin drop'], ['timer', 'Constant']] as const).filter(([m]) => !isVending || m !== 'timer').map(([m, label]) => (
              <TouchableOpacity key={m} style={[styles.segBtn, ssMode === m && styles.segBtnActive]} onPress={() => setSsMode(m)}>
                <Text style={[styles.segText, ssMode === m && styles.segTextActive]}>{label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {ssMode === 'none' && ctrls.length > 0 && <Text style={styles.warn}>No relay assigned: customers will see "not set up yet" and cannot pay until a {isVending ? 'coin drop' : 'coin drop, a constant output'} or an Advanced action exists.</Text>}
          {ssMode !== 'none' && (() => {
            const cur = ssMode === 'coin' ? coin : timer;
            const setCur = (patch: { controllerId?: string; relay?: string }) => (ssMode === 'coin' ? setCoin({ ...coin, ...patch }) : setTimer({ ...timer, ...patch }));
            return (
              <View style={styles.boardCard}>
                {ctrls.length > 1 && <Picker label="Board" options={ctrlOptions} value={cur.controllerId} onValueChange={v => setCur({ controllerId: v, relay: '' })} />}
                {ctrls.length === 1 && cur.controllerId !== ctrls[0].controllerId ? (setCur({ controllerId: ctrls[0].controllerId }), null) : null}
                <Picker label={ssMode === 'coin' ? 'Coin drop relay' : 'Constant output relay'} options={relayOptions(cur.controllerId, ssMode === 'coin' ? 'coin' : 'timer')} value={cur.relay} onValueChange={v => setCur({ relay: v })} />
                {ssMode === 'coin' && !isVending && <Input label="Coin value (¢ per pulse)" value={coin.coinValueCents} onChangeText={v => setCoin({ ...coin, coinValueCents: v.replace(/[^0-9]/g, '') })} keyboardType="number-pad" />}
              </View>
            );
          })()}
        </>
      )}

      {!isVending && (<>
      {/* ---- Queue + Out of service (10-15: split headers) ---- */}
      {isAutomatic && <Text style={styles.sectionTitle}>Queue Wash Purchases</Text>}
      {(live.inUse || live.outOfService) && <Text style={styles.warn}>Right now: {live.outOfService ? 'OUT OF SERVICE' : 'IN USE'}</Text>}
      {/* Nathan 10-18: queueing keys off the Wash in progress input. No inputs on any board = nothing to wait for, purchases start right away. */}
      {isAutomatic && inputBoards.length === 0 && <Text style={styles.help}>None of this bay's boards has digital inputs, so the bay cannot tell when a wash is in progress. Purchases start right away.</Text>}
      {isAutomatic && inputBoards.length > 0 && (<>
        <Picker label="Wash in progress signal" options={inOpOptions} value={inOpValue} onValueChange={setInOpInput} />
        {!inOpValue && <Text style={styles.help}>Pick the input wired to the wash's in-progress signal. Until one is set, purchases start right away.</Text>}
        {!!inOpValue && <Input label="Delay after the Wash in progress signal clears before a queued wash starts (ms, 0 = purchases blocked while a wash is in progress)" value={startDelayMs} onChangeText={v => setStartDelayMs(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" />}
        {!!inOpValue && (parseInt(startDelayMs) || 0) > 0 && <Input label="Max wait for a queued wash (minutes). Only applies when a queued wash is stuck behind a wash that stays in progress; after this it is refunded automatically." value={maxWaitMin} onChangeText={v => setMaxWaitMin(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" />}
      </>)}
      <Text style={styles.sectionTitle}>Out of Service</Text>
      <Input label="Out-of-service message to customers" placeholder={cfg.bay.oosMessageEffective} value={oosMessage} onChangeText={setOosMessage} maxLength={200} />
      <View style={styles.switchRow}>
        <Text style={styles.switchLabel}>Email me when the bay goes out of service</Text>
        <Switch value={alertOnOut} onValueChange={setAlertOnOut} trackColor={{ false: colors.borderLight, true: colors.primary + '80' }} thumbColor={alertOnOut ? colors.primary : colors.textLight} />
      </View>
      <View style={styles.switchRow}>
        <Text style={styles.switchLabel}>Email me when it is back in service</Text>
        <Switch value={alertOnBack} onValueChange={setAlertOnBack} trackColor={{ false: colors.borderLight, true: colors.primary + '80' }} thumbColor={alertOnBack ? colors.primary : colors.textLight} />
      </View>
      <Input label="Ignore outages shorter than (seconds)" value={flapSec} onChangeText={v => setFlapSec(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" />

      {issues.length > 0 && (
        <View style={styles.issues}>{issues.map((m, i) => <Text key={i} style={styles.issue}>• {m}</Text>)}</View>
      )}
      </>)}
      {!hideSave && <Button title="Save Equipment Configuration" onPress={save} loading={saving} size="lg" style={{ marginTop: spacing.md }} />}
    </View>
  );
});

export default BayEquipmentConfig;

const createStyles = (c: ColorTheme) => StyleSheet.create({
  center: { padding: spacing.xl, alignItems: 'center' },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.lg, marginBottom: spacing.xs },
  sub: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.md, marginBottom: spacing.xs },
  help: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.sm },
  warn: { fontSize: fontSize.xs, color: c.warning, marginBottom: spacing.xs, fontWeight: fontWeight.semibold },
  empty: { fontSize: fontSize.sm, color: c.textLight, marginBottom: spacing.sm },
  ctrlRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: c.borderLight },
  dot: { width: 10, height: 10, borderRadius: 5 },
  ctrlName: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  ctrlMeta: { fontSize: fontSize.xs, color: c.textSecondary },
  segment: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm, flexWrap: 'wrap' },
  segBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.xs, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.border, backgroundColor: c.surfaceSecondary },
  segBtnActive: { borderColor: c.primary, backgroundColor: c.primary + '15' },
  segText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.semibold },
  segTextActive: { color: c.primary },
  mapRow: { paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: c.borderLight },
  boardInputs: { marginTop: spacing.xs, marginBottom: spacing.sm },
  boardInputsTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: 2 },
  tierPrompt: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: spacing.sm, padding: spacing.sm, borderRadius: borderRadius.md, backgroundColor: c.primary + '12', marginBottom: spacing.sm },
  tierPromptText: { flex: 1, minWidth: 200, fontSize: fontSize.sm, color: c.text },
  boardCard: { borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, padding: spacing.sm, marginBottom: spacing.sm, backgroundColor: c.surface },
  boardHead: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs, flexWrap: 'wrap' },
  boardTitle: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  modelBadge: { paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: 10, backgroundColor: c.primary + '15', borderWidth: 1, borderColor: c.primary },
  modelBadgeText: { fontSize: 11, color: c.primary, fontWeight: fontWeight.semibold },
  relayRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: 4, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: c.borderLight },
  relayLabel: { width: 34, fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.textSecondary },
  mapHead: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  mapName: { fontSize: fontSize.sm, color: c.text, fontWeight: fontWeight.medium, flex: 1 },
  mapFields: { flexDirection: 'row', gap: spacing.sm, alignItems: 'flex-end', marginTop: spacing.xs, flexWrap: 'wrap' },
  inputRow: { paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: c.borderLight },
  inputHead: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs },
  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.md, paddingVertical: spacing.sm },
  switchLabel: { flex: 1, fontSize: fontSize.sm, color: c.text },
  issues: { marginTop: spacing.sm, padding: spacing.sm, backgroundColor: c.danger + '12', borderRadius: borderRadius.md },
  issue: { fontSize: fontSize.xs, color: c.danger },
});
